import { useState } from 'react'
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Eye,
  FileText,
  Layers,
  Mail,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Settings2,
  Shuffle,
  TestTube2,
  X,
  Zap,
} from 'lucide-react'
import { withDemo } from '@/config/demoMode'
import BlankSlate from '@/components/shared/BlankSlate'
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type RuleStatus = 'Active' | 'Paused'
type TriggerType = 'Deal Stage Change' | 'Date/Timer' | 'Metric Threshold' | 'Email Received' | 'Document Status' | 'Schedule'

interface AutomationRule {
  id: string
  name: string
  triggerEvent: string
  triggerType: TriggerType
  conditions: string
  actions: string[]
  module: string
  status: RuleStatus
  lastTriggered: string
  timesTriggered: number
  createdBy: string
  description: string
}

// ---------------------------------------------------------------------------
// Mock Data — 15 Automation Rules
// ---------------------------------------------------------------------------
const DEMO_AUTOMATION_RULES: AutomationRule[] = [
  {
    id: 'AUT-001',
    name: 'Auto-notify counsel on commitment',
    triggerEvent: 'Deal stage -> Commitment Issued',
    triggerType: 'Deal Stage Change',
    conditions: 'Deal Amount > $25M',
    actions: ['Email outside counsel', 'Generate engagement letter', 'Create closing checklist'],
    module: 'Closing',
    status: 'Active',
    lastTriggered: '2026-03-18',
    timesTriggered: 47,
    createdBy: 'Sarah Chen',
    description: 'Automatically notifies outside counsel and generates an engagement letter template when any deal transitions to Commitment Issued stage.',
  },
  {
    id: 'AUT-002',
    name: 'Rate lock alert',
    triggerEvent: 'Rate lock expiry in 48 hours',
    triggerType: 'Date/Timer',
    conditions: 'Rate Lock status = Active',
    actions: ['Notify originator', 'Notify MD', 'Alert hedge desk', 'Add to daily brief'],
    module: 'Closing',
    status: 'Active',
    lastTriggered: '2026-03-17',
    timesTriggered: 23,
    createdBy: 'Marcus Rivera',
    description: 'Sends urgent notifications to the originator, managing director, and hedge desk when a rate lock is within 48 hours of expiration.',
  },
  {
    id: 'AUT-003',
    name: 'Appraisal overdue escalation',
    triggerEvent: 'Appraisal ordered > 7 days ago',
    triggerType: 'Date/Timer',
    conditions: 'Appraisal status != Received AND days > 7',
    actions: ['Escalate to asset manager', 'Add to watchlist', 'Send borrower reminder'],
    module: 'Underwriting',
    status: 'Active',
    lastTriggered: '2026-03-15',
    timesTriggered: 31,
    createdBy: 'Jessica Patel',
    description: 'Escalates to the asset manager and adds to the watchlist when an ordered appraisal has not been received within 7 business days.',
  },
  {
    id: 'AUT-004',
    name: 'Auto-generate ASR',
    triggerEvent: 'Deal stage -> Application Back',
    triggerType: 'Deal Stage Change',
    conditions: 'All required documents uploaded',
    actions: ['Generate Asset Summary Report', 'Notify underwriter', 'Queue for review'],
    module: 'Reports',
    status: 'Active',
    lastTriggered: '2026-03-18',
    timesTriggered: 89,
    createdBy: 'David Cruz',
    description: 'Automatically generates a draft Asset Summary Report when a deal transitions to Application Back with all required documents uploaded.',
  },
  {
    id: 'AUT-005',
    name: 'New deal intake',
    triggerEvent: 'Email received with keywords',
    triggerType: 'Email Received',
    conditions: 'Subject contains "financing request" OR "loan inquiry"',
    actions: ['Create deal entry', 'Notify intake team', 'Extract borrower data', 'Auto-populate fields'],
    module: 'Intake',
    status: 'Active',
    lastTriggered: '2026-03-18',
    timesTriggered: 156,
    createdBy: 'Sarah Chen',
    description: 'Monitors the deal inbox for financing requests and automatically creates a new deal entry with extracted borrower and property data.',
  },
  {
    id: 'AUT-006',
    name: 'Committee packet assembly',
    triggerEvent: 'Memo status = Approved',
    triggerType: 'Document Status',
    conditions: 'All approvals collected AND risk rating assigned',
    actions: ['Compile committee packet', 'Schedule committee date', 'Notify committee members', 'Reserve conference room'],
    module: 'Committee',
    status: 'Active',
    lastTriggered: '2026-03-14',
    timesTriggered: 34,
    createdBy: 'Michael Grant',
    description: 'Compiles the full committee packet and schedules a committee date when the credit memo receives final approval status.',
  },
  {
    id: 'AUT-007',
    name: 'Covenant breach alert',
    triggerEvent: 'DSCR metric < threshold',
    triggerType: 'Metric Threshold',
    conditions: 'DSCR < 1.10x on trailing quarter',
    actions: ['Notify surveillance team', 'Add to watchlist', 'Generate exception report', 'Flag for committee review'],
    module: 'Surveillance',
    status: 'Active',
    lastTriggered: '2026-03-12',
    timesTriggered: 18,
    createdBy: 'Lauren Kim',
    description: 'Triggers a full exception workflow when any funded deal DSCR falls below 1.10x on the trailing quarterly calculation.',
  },
  {
    id: 'AUT-008',
    name: 'Insurance renewal reminder',
    triggerEvent: 'Insurance expiry in 30 days',
    triggerType: 'Date/Timer',
    conditions: 'Deal status = Funded AND insurance on file',
    actions: ['Notify borrower', 'Notify asset manager', 'Create task', 'Set 15-day follow-up'],
    module: 'Surveillance',
    status: 'Active',
    lastTriggered: '2026-03-10',
    timesTriggered: 62,
    createdBy: 'Emily Scott',
    description: 'Sends insurance renewal reminders to borrowers and asset managers 30 days before policy expiration on funded deals.',
  },
  {
    id: 'AUT-009',
    name: 'Closing checklist completion',
    triggerEvent: 'All checklist items = Complete',
    triggerType: 'Document Status',
    conditions: 'Closing checklist 100% AND wire instructions on file',
    actions: ['Notify closer', 'Generate funding memo', 'Request wire approval', 'Update deal stage'],
    module: 'Closing',
    status: 'Active',
    lastTriggered: '2026-03-16',
    timesTriggered: 42,
    createdBy: 'Sarah Chen',
    description: 'Triggers the funding workflow when all closing checklist items are marked complete and wire instructions are verified.',
  },
  {
    id: 'AUT-010',
    name: 'Monthly borrower reporting',
    triggerEvent: '1st of every month',
    triggerType: 'Schedule',
    conditions: 'Deal status = Funded',
    actions: ['Send report request to all borrowers', 'Create reporting tasks', 'Set 10-day deadline'],
    module: 'Surveillance',
    status: 'Active',
    lastTriggered: '2026-03-01',
    timesTriggered: 8,
    createdBy: 'Peter Morgan',
    description: 'Sends automated monthly reporting requests to all borrowers on funded deals on the first business day of each month.',
  },
  {
    id: 'AUT-011',
    name: 'Sponsor credit refresh',
    triggerEvent: 'Every 90 days from origination',
    triggerType: 'Schedule',
    conditions: 'Deal status IN (Funded, Rate Lock, Commitment)',
    actions: ['Request updated PFS', 'Request tax returns', 'Notify borrower', 'Create compliance task'],
    module: 'Asset Mgmt',
    status: 'Paused',
    lastTriggered: '2026-02-15',
    timesTriggered: 24,
    createdBy: 'Jessica Patel',
    description: 'Requests updated personal financial statements and tax returns from sponsors on a 90-day rolling cycle.',
  },
  {
    id: 'AUT-012',
    name: 'Post-close surveillance setup',
    triggerEvent: 'Deal stage -> Funded',
    triggerType: 'Deal Stage Change',
    conditions: 'Funding confirmed AND servicing transferred',
    actions: ['Create surveillance profile', 'Set covenant triggers', 'Schedule first review', 'Notify surveillance team'],
    module: 'Surveillance',
    status: 'Active',
    lastTriggered: '2026-03-16',
    timesTriggered: 39,
    createdBy: 'Lauren Kim',
    description: 'Automatically creates a surveillance profile with covenant trigger thresholds when a deal reaches funded status.',
  },
  {
    id: 'AUT-013',
    name: 'Large loan MD escalation',
    triggerEvent: 'New deal created',
    triggerType: 'Deal Stage Change',
    conditions: 'Deal Amount > $100M',
    actions: ['Notify Managing Director', 'Flag as priority', 'Assign senior originator', 'Schedule intro call'],
    module: 'Intake',
    status: 'Active',
    lastTriggered: '2026-03-17',
    timesTriggered: 12,
    createdBy: 'Marcus Rivera',
    description: 'Escalates any new deal over $100M directly to the managing director with priority flagging and senior originator assignment.',
  },
  {
    id: 'AUT-014',
    name: 'Environmental report expiry',
    triggerEvent: 'Phase I report age > 180 days',
    triggerType: 'Date/Timer',
    conditions: 'Deal stage IN (Underwriting, Committee, Closing)',
    actions: ['Notify underwriter', 'Order Phase I update', 'Add to checklist', 'Flag in committee memo'],
    module: 'Underwriting',
    status: 'Active',
    lastTriggered: '2026-03-08',
    timesTriggered: 15,
    createdBy: 'David Cruz',
    description: 'Flags environmental reports older than 180 days on active deals and initiates a Phase I update order.',
  },
  {
    id: 'AUT-015',
    name: 'Weekly pipeline digest',
    triggerEvent: 'Every Monday at 7:00 AM',
    triggerType: 'Schedule',
    conditions: 'Always',
    actions: ['Generate pipeline summary', 'Email to leadership team', 'Post to Slack channel', 'Archive report'],
    module: 'Reports',
    status: 'Active',
    lastTriggered: '2026-03-17',
    timesTriggered: 52,
    createdBy: 'Michael Grant',
    description: 'Compiles a weekly pipeline digest with deal movement, stage transitions, and KPI trends and distributes to the leadership team.',
  },
]

// ---------------------------------------------------------------------------
// Rule Builder state types
// ---------------------------------------------------------------------------
type WhenOption = 'Deal Stage Change' | 'Date/Timer' | 'Metric Threshold' | 'Email Received' | 'Document Status' | 'Schedule'
type ActionOption = 'Send Notification' | 'Generate Report' | 'Update Stage' | 'Create Task' | 'Email External Party' | 'Add to Watchlist'

const WHEN_OPTIONS: WhenOption[] = ['Deal Stage Change', 'Date/Timer', 'Metric Threshold', 'Email Received', 'Document Status', 'Schedule']
const ACTION_OPTIONS: ActionOption[] = ['Send Notification', 'Generate Report', 'Update Stage', 'Create Task', 'Email External Party', 'Add to Watchlist']
const MODULE_OPTIONS = ['All Modules', 'Intake', 'Screening', 'Underwriting', 'Committee', 'Closing', 'Surveillance', 'Asset Mgmt', 'Reports']
const CONDITION_FIELDS = ['Deal Amount', 'DSCR', 'LTV', 'Deal Stage', 'Property Type', 'Days Open', 'Priority']
const CONDITION_OPS = ['>', '<', '>=', '<=', '=', '!=', 'contains', 'in']

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function WorkflowAutomation() {
  const [selectedRule, setSelectedRule] = useState<AutomationRule | null>(null)
  const [statusFilter, setStatusFilter] = useState<RuleStatus | ''>('')
  const [builderOpen, setBuilderOpen] = useState(false)

  // Rule builder state
  const [builderWhen, setBuilderWhen] = useState<WhenOption>('Deal Stage Change')
  const [builderField, setBuilderField] = useState('Deal Amount')
  const [builderOp, setBuilderOp] = useState('>')
  const [builderValue, setBuilderValue] = useState('$50M')
  const [builderActions, setBuilderActions] = useState<ActionOption[]>(['Send Notification'])
  const [builderModule, setBuilderModule] = useState('All Modules')

  // Gated at render so the module's own demo switch governs it too.
  const AUTOMATION_RULES = withDemo(DEMO_AUTOMATION_RULES, [] as AutomationRule[], 'workflow-automation')
  const filteredRules = statusFilter ? AUTOMATION_RULES.filter((r) => r.status === statusFilter) : AUTOMATION_RULES

  // Metrics
  const activeRules = AUTOMATION_RULES.filter((r) => r.status === 'Active').length
  const triggersToday = AUTOMATION_RULES.filter((r) => r.lastTriggered === '2026-03-18').length
  const automationsMtd = AUTOMATION_RULES.reduce((s, r) => s + (r.lastTriggered >= '2026-03-01' ? 1 : 0), 0)
  const totalTriggered = AUTOMATION_RULES.reduce((s, r) => s + r.timesTriggered, 0)
  const moduleGroups = [...new Set(AUTOMATION_RULES.map((r) => r.module))].length

  const toggleAction = (action: ActionOption) => {
    setBuilderActions((prev) =>
      prev.includes(action) ? prev.filter((a) => a !== action) : [...prev, action]
    )
  }

  if (AUTOMATION_RULES.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-4 py-3">
        <BlankSlate
          icon={Zap}
          title="No automation rules configured yet"
          description="Workflow automation runs trigger-condition-action rules across your pipeline — auto-notifying counsel, escalating overdue items, generating reports, and more. Your active rules and their activity will appear here once you create them."
          steps={[
            'Define a trigger (deal stage change, timer, metric threshold, or schedule).',
            'Add conditions and the actions the rule should perform.',
            'Save the rule to activate it and track its run history here.',
          ]}
          actions={[
            { label: 'Open Settings', to: '/settings' },
            { label: 'View Pipeline', to: '/pipeline', variant: 'secondary' },
          ]}
        />
      </div>
    )
  }

  return (
    <div className="page-fill gap-2 p-3">
      {/* Metric Strip */}
      <section className="grid flex-shrink-0 gap-2 md:grid-cols-3 xl:grid-cols-5">
        {[
          { label: 'Active Rules', value: String(activeRules), sub: `${AUTOMATION_RULES.length - activeRules} paused`, icon: Zap, color: 'text-emerald-400' },
          { label: 'Triggers Today', value: String(triggersToday), sub: 'events processed', icon: Bell, color: 'text-amber-300' },
          { label: 'Automations Run (MTD)', value: String(totalTriggered), sub: `${automationsMtd} rules active this month`, icon: RefreshCw, color: 'text-stone-100' },
          { label: 'Avg Time Saved', value: '4.2 hrs', sub: 'per trigger vs. manual', icon: Clock, color: 'text-emerald-400' },
          { label: 'Rules by Module', value: `${moduleGroups} modules`, sub: 'cross-workflow coverage', icon: Layers, color: 'text-amber-200' },
        ].map((m) => (
          <div key={m.label} className="card px-4 py-3">
            <div className="flex items-center gap-1.5">
              <m.icon size={12} className="text-stone-600" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-stone-600">{m.label}</span>
            </div>
            <div className={`mt-2 text-xl font-semibold ${m.color}`}>{m.value}</div>
            <div className="mt-1 text-[11px] text-stone-500">{m.sub}</div>
          </div>
        ))}
      </section>

      {/* Active Rules Table */}
      <section className="card flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/10 px-4 py-2">
          <div>
            <div className="flex items-center gap-2">
              <Settings2 size={14} className="text-amber-400" />
              <span className="terminal-kicker">Active Automation Rules</span>
            </div>
            <p className="mt-0.5 text-xs text-stone-500">Click any rule for details. Toggle status to pause/resume.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBuilderOpen(true)}
              className="btn-primary flex items-center gap-1.5 text-[11px]"
            >
              <Plus size={12} />
              Create Rule
            </button>
            <div className="relative">
              <select
                className="appearance-none rounded-md border border-amber-400/10 bg-black/40 py-1.5 pr-7 pl-2.5 text-[11px] text-stone-300 outline-none focus:border-amber-400/30"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as RuleStatus | '')}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Paused">Paused</option>
              </select>
              <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-stone-600" />
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {['Rule Name', 'Trigger Event', 'Conditions', 'Actions', 'Module', 'Status', 'Last Triggered', 'Times'].map((h) => (
                  <th
                    key={h}
                    style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8c8c8c', borderBottom: '1px solid var(--line-soft)', background: 'rgba(0,0,0,0.25)' }}
                    className="px-3 py-1.5 text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRules.map((rule) => (
                <tr
                  key={rule.id}
                  className={`cursor-pointer border-b border-amber-400/6 transition-colors hover:bg-amber-400/[0.04] ${selectedRule?.id === rule.id ? 'bg-amber-400/[0.08]' : ''}`}
                  onClick={() => setSelectedRule(selectedRule?.id === rule.id ? null : rule)}
                >
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <Zap size={11} className={rule.status === 'Active' ? 'text-amber-400' : 'text-stone-600'} />
                      <span className="text-xs font-medium text-stone-100">{rule.name}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-xs text-stone-400">{rule.triggerEvent}</td>
                  <td className="max-w-[180px] truncate px-3 py-1.5 text-xs text-stone-500">{rule.conditions}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className="text-[10px] text-stone-400">{rule.actions.length} action{rule.actions.length !== 1 ? 's' : ''}</span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className="rounded-full border border-amber-400/15 bg-amber-400/[0.06] px-2 py-0.5 text-[10px] text-amber-200">{rule.module}</span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${
                      rule.status === 'Active'
                        ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300'
                        : 'border-stone-500/25 bg-stone-500/10 text-stone-400'
                    }`}>
                      {rule.status === 'Active' ? <Play size={8} /> : <Pause size={8} />}
                      {rule.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-400">{rule.lastTriggered}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-300">{rule.timesTriggered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Selected Rule Detail */}
        {selectedRule && (
          <div className="border-t border-amber-400/10 bg-black/20 px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-amber-400" />
                  <span className="text-sm font-semibold text-stone-100">{selectedRule.name}</span>
                  <span className="text-[10px] font-mono text-stone-600">{selectedRule.id}</span>
                </div>
                <p className="text-xs leading-5 text-stone-400">{selectedRule.description}</p>
                <div className="flex flex-wrap gap-4 pt-1">
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.16em] text-stone-600">Trigger</div>
                    <div className="mt-0.5 text-xs text-stone-200">{selectedRule.triggerEvent}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.16em] text-stone-600">Conditions</div>
                    <div className="mt-0.5 text-xs text-stone-200">{selectedRule.conditions}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.16em] text-stone-600">Created By</div>
                    <div className="mt-0.5 text-xs text-stone-200">{selectedRule.createdBy}</div>
                  </div>
                </div>
                <div className="pt-1">
                  <div className="text-[9px] uppercase tracking-[0.16em] text-stone-600">Actions</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {selectedRule.actions.map((a) => (
                      <span key={a} className="rounded-full border border-amber-400/15 bg-amber-400/[0.06] px-2 py-0.5 text-[10px] text-amber-200">{a}</span>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedRule(null) }}
                className="rounded p-1 text-stone-600 hover:text-amber-300"
              >
                <span className="text-xs">Close</span>
              </button>
            </div>
          </div>
        )}
      </section>

      {builderOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setBuilderOpen(false)} role="presentation">
      <section className="card max-h-[88vh] w-full max-w-5xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-amber-400/10 px-4 py-3">
          <div>
            <div className="flex items-center gap-2">
              <Plus size={14} className="text-amber-400" />
              <span className="terminal-kicker">Create Automation Rule</span>
            </div>
            <p className="mt-1 text-xs text-stone-500">Visual no-code rule builder. Define trigger, conditions, and actions.</p>
          </div>
          <button type="button" onClick={() => setBuilderOpen(false)} className="rounded p-1 text-stone-500 hover:text-amber-200" aria-label="Close rule builder">
            <X size={14} />
          </button>
        </div>

        <div className="p-4">
          {/* Flow: WHEN -> IF -> THEN */}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-0">

            {/* WHEN block */}
            <div className="flex-1 rounded-xl border border-amber-400/15 bg-amber-400/[0.03] p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400/20 text-[10px] font-bold text-amber-300">1</div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-300">When</span>
                <span className="text-[10px] text-stone-500">(Trigger Event)</span>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <select
                    className="w-full appearance-none rounded-lg border border-amber-400/15 bg-black/50 py-2 pr-8 pl-3 text-xs text-stone-200 outline-none focus:border-amber-400/30"
                    value={builderWhen}
                    onChange={(e) => setBuilderWhen(e.target.value as WhenOption)}
                  >
                    {WHEN_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  <ChevronDown size={10} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-stone-600" />
                </div>
                <div className="rounded-lg border border-amber-400/8 bg-black/20 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
                    <Shuffle size={10} />
                    {builderWhen === 'Deal Stage Change' && 'Fires when a deal transitions to a new stage'}
                    {builderWhen === 'Date/Timer' && 'Fires based on a date field or countdown timer'}
                    {builderWhen === 'Metric Threshold' && 'Fires when a credit metric crosses a threshold'}
                    {builderWhen === 'Email Received' && 'Fires when an email matches keyword pattern'}
                    {builderWhen === 'Document Status' && 'Fires when document status changes'}
                    {builderWhen === 'Schedule' && 'Fires on a recurring schedule (cron)'}
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow connector */}
            <div className="hidden lg:flex lg:flex-col lg:items-center lg:justify-center lg:px-3 lg:pt-8">
              <div className="h-px w-8 bg-amber-400/20" />
              <ArrowRight size={14} className="text-amber-400/40" />
            </div>
            <div className="flex items-center justify-center lg:hidden">
              <ArrowRight size={14} className="rotate-90 text-amber-400/40" />
            </div>

            {/* IF block */}
            <div className="flex-1 rounded-xl border border-blue-400/15 bg-blue-400/[0.03] p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-400/20 text-[10px] font-bold text-blue-300">2</div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-300">If</span>
                <span className="text-[10px] text-stone-500">(Conditions)</span>
              </div>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <select
                      className="w-full appearance-none rounded-lg border border-blue-400/15 bg-black/50 py-2 pr-8 pl-3 text-xs text-stone-200 outline-none focus:border-blue-400/30"
                      value={builderField}
                      onChange={(e) => setBuilderField(e.target.value)}
                    >
                      {CONDITION_FIELDS.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <ChevronDown size={10} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-stone-600" />
                  </div>
                  <div className="relative w-20">
                    <select
                      className="w-full appearance-none rounded-lg border border-blue-400/15 bg-black/50 py-2 pr-6 pl-3 text-xs text-stone-200 outline-none focus:border-blue-400/30"
                      value={builderOp}
                      onChange={(e) => setBuilderOp(e.target.value)}
                    >
                      {CONDITION_OPS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                    <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-stone-600" />
                  </div>
                  <input
                    type="text"
                    className="w-28 rounded-lg border border-blue-400/15 bg-black/50 px-3 py-2 text-xs text-stone-200 outline-none focus:border-blue-400/30"
                    value={builderValue}
                    onChange={(e) => setBuilderValue(e.target.value)}
                    placeholder="Value..."
                  />
                </div>
                <button className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300">
                  <Plus size={10} /> Add condition
                </button>
                <div className="rounded-lg border border-blue-400/8 bg-black/20 px-3 py-2">
                  <div className="font-mono text-[10px] text-blue-300">
                    {builderField} {builderOp} {builderValue}
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow connector */}
            <div className="hidden lg:flex lg:flex-col lg:items-center lg:justify-center lg:px-3 lg:pt-8">
              <div className="h-px w-8 bg-emerald-400/20" />
              <ArrowRight size={14} className="text-emerald-400/40" />
            </div>
            <div className="flex items-center justify-center lg:hidden">
              <ArrowRight size={14} className="rotate-90 text-emerald-400/40" />
            </div>

            {/* THEN block */}
            <div className="flex-1 rounded-xl border border-emerald-400/15 bg-emerald-400/[0.03] p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/20 text-[10px] font-bold text-emerald-300">3</div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300">Then</span>
                <span className="text-[10px] text-stone-500">(Actions)</span>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-1.5">
                  {ACTION_OPTIONS.map((action) => {
                    const selected = builderActions.includes(action)
                    const icons: Record<ActionOption, typeof Bell> = {
                      'Send Notification': Bell,
                      'Generate Report': FileText,
                      'Update Stage': Shuffle,
                      'Create Task': CheckCircle2,
                      'Email External Party': Mail,
                      'Add to Watchlist': Eye,
                    }
                    const Icon = icons[action]
                    return (
                      <button
                        key={action}
                        onClick={() => toggleAction(action)}
                        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-[10px] transition-all ${
                          selected
                            ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                            : 'border-white/8 bg-white/[0.02] text-stone-500 hover:border-emerald-400/15 hover:text-stone-300'
                        }`}
                      >
                        {selected ? <Check size={10} className="text-emerald-400" /> : <Icon size={10} />}
                        {action}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Module Scope + Actions */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-amber-400/8 pt-4">
            <div className="flex items-center gap-3">
              <div>
                <span className="text-[9px] uppercase tracking-[0.16em] text-stone-600">Module Scope</span>
                <div className="relative mt-1">
                  <select
                    className="appearance-none rounded-lg border border-amber-400/10 bg-black/40 py-1.5 pr-7 pl-2.5 text-[11px] text-stone-300 outline-none focus:border-amber-400/30"
                    value={builderModule}
                    onChange={(e) => setBuilderModule(e.target.value)}
                  >
                    {MODULE_OPTIONS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-stone-600" />
                </div>
              </div>

              {/* Rule preview */}
              <div className="rounded-lg border border-amber-400/8 bg-black/20 px-3 py-2">
                <div className="text-[9px] uppercase tracking-[0.12em] text-stone-600 mb-1">Rule Preview</div>
                <div className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-amber-300">WHEN</span>
                  <span className="text-stone-400">{builderWhen}</span>
                  <ArrowRight size={8} className="text-stone-600" />
                  <span className="text-blue-300">IF</span>
                  <span className="font-mono text-stone-400">{builderField} {builderOp} {builderValue}</span>
                  <ArrowRight size={8} className="text-stone-600" />
                  <span className="text-emerald-300">THEN</span>
                  <span className="text-stone-400">{builderActions.join(', ')}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button className="flex items-center gap-1.5 rounded-lg border border-amber-400/15 bg-amber-400/[0.06] px-3 py-1.5 text-[11px] text-amber-200 transition-colors hover:bg-amber-400/[0.12]">
                <TestTube2 size={12} />
                Test Rule
              </button>
              <button className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium text-[#0d0d0d] transition-all" style={{ background: 'linear-gradient(135deg, rgba(246,223,154,0.98), rgba(196,154,67,0.96))', boxShadow: '0 12px 30px rgba(196,154,67,0.22)' }}>
                <Check size={12} />
                Save Rule
              </button>
            </div>
          </div>
        </div>
      </section>
      </div>
      )}

      {/* Rule Activity Summary */}
      <section className="grid min-h-0 flex-shrink-0 gap-2 overflow-hidden xl:grid-cols-3">
        {/* By Module */}
        <div className="card px-4 py-3">
          <div className="flex items-center gap-2">
            <Layers size={14} className="text-amber-400" />
            <span className="terminal-kicker">Rules by Module</span>
          </div>
          <div className="mt-3 space-y-2">
            {(() => {
              const modules = [...new Set(AUTOMATION_RULES.map((r) => r.module))]
              return modules.map((mod) => {
                const count = AUTOMATION_RULES.filter((r) => r.module === mod).length
                const total = AUTOMATION_RULES.filter((r) => r.module === mod).reduce((s, r) => s + r.timesTriggered, 0)
                return (
                  <div key={mod} className="flex items-center justify-between rounded-lg border border-amber-400/8 bg-white/[0.015] px-3 py-2">
                    <div>
                      <div className="text-xs font-medium text-stone-100">{mod}</div>
                      <div className="text-[10px] text-stone-500">{count} rule{count !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-xs text-amber-300">{total}</div>
                      <div className="text-[10px] text-stone-600">triggers</div>
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        </div>

        {/* Recent Triggers */}
        <div className="card px-4 py-3">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-amber-400" />
            <span className="terminal-kicker">Recent Activity</span>
          </div>
          <div className="mt-3 space-y-2">
            {AUTOMATION_RULES
              .slice()
              .sort((a, b) => b.lastTriggered.localeCompare(a.lastTriggered))
              .slice(0, 6)
              .map((rule) => (
                <div key={rule.id} className="flex items-start gap-2 rounded-lg border border-amber-400/8 bg-white/[0.015] px-3 py-2">
                  <Zap size={11} className="mt-0.5 flex-shrink-0 text-amber-400" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs text-stone-100">{rule.name}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] text-stone-500">
                      <span className="font-mono">{rule.lastTriggered}</span>
                      <span className="text-stone-700">|</span>
                      <span>{rule.actions[0]}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Top Performers */}
        <div className="card px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" />
            <span className="terminal-kicker">Top Performers</span>
          </div>
          <div className="mt-3 space-y-2">
            {AUTOMATION_RULES
              .slice()
              .sort((a, b) => b.timesTriggered - a.timesTriggered)
              .slice(0, 6)
              .map((rule, i) => (
                <div key={rule.id} className="flex items-center justify-between rounded-lg border border-amber-400/8 bg-white/[0.015] px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-400/10 text-[9px] font-bold text-amber-300">
                      {i + 1}
                    </span>
                    <div className="text-xs text-stone-100">{rule.name}</div>
                  </div>
                  <div className="font-mono text-xs text-amber-300">{rule.timesTriggered}x</div>
                </div>
              ))}
          </div>
        </div>
      </section>
    </div>
  )
}
